import { useI18n } from "@core/i18n/i18n";
import {
	PatientProgram,
	PatientProgramEditionMetadata,
	PatientProgramEditor,
	PatientProgramProfile,
} from "@domain/program/patient-program";
import { extractPropsForProgramStatusComponentFromPatientProgram } from "@domain/program/program-status/program-status-utils";
import { TemplateItem } from "@domain/template/template";
import { useUser } from "@domain/user/hooks/use-user";
import { e2eTestId } from "@e2e/test-ids";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { MenuButton, MenuItemProps } from "@ui/components/common/menu-button";
import { ProgramStatusComponent } from "@ui/components/program/program-status-component";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { colorWithAlpha } from "@utils/color-utils";
import { formatName } from "@utils/string-utils";
import { HTMLMotionProps } from "framer-motion";
import React, { useState } from "react";
import { PatientButton } from "@ui/components/common/Patient-button";

const SPACE_SEPARATOR = " ";

interface CommonCardProps extends HTMLMotionProps<"div"> {
	cardActions?: MenuItemProps[];
	onClick?: () => void;
	isNew?: boolean;
}

interface ProgramCardProps extends CommonCardProps {
	title: string;
	sessionsPerWeek: number;
	exercisesPerWeek: number;
	status?: React.ReactNode;
	lastEditionMetadata?: PatientProgramEditionMetadata;
	patient?: PatientProgramProfile;
}

export const PatientProgramCard: React.FC<
	{ program: PatientProgram } & { patient?: PatientProgramProfile } & CommonCardProps
> = ({ program, patient, isNew, ...props }) => (
	<ProgramCard
		id={program.id}
		title={program.name}
		sessionsPerWeek={program.sessionsPerWeek}
		exercisesPerWeek={program.exercisesPerWeek}
		status={
			<ProgramStatusComponent
				programStatus={extractPropsForProgramStatusComponentFromPatientProgram(program, true)}
				statusLabelVisible
			/>
		}
		lastEditionMetadata={program.lastEditionMetadata}
		isNew={isNew}
		patient={patient}
		{...props}
	/>
);

interface TemplateCardProps extends CommonCardProps {
	template: TemplateItem;
	editor: PatientProgramEditor;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({ editor, template, ...props }) => (
	<ProgramCard
		id={template.id}
		title={template.name}
		sessionsPerWeek={template.sessionsPerWeek}
		exercisesPerWeek={template.exercisesPerWeek}
		lastEditionMetadata={{ date: template.lastEditionDate, editor }}
		{...props}
	/>
);

const ProgramCard: React.FC<ProgramCardProps> = ({
	title,
	sessionsPerWeek,
	exercisesPerWeek,
	status,
	lastEditionMetadata,
	cardActions,
	onClick,
	isNew = false,
	patient,
	...props
}) => {
	const { format, formatRelativeDateTime } = useI18n();
	const user = useUser();
	const { isTouchDevice } = useDimensions();

	const [isHovering, setIsHovering] = useState(false);

	return (
		<Container
			data-test={e2eTestId.patientPage.programCard}
			onClick={onClick}
			isNew={isNew}
			onMouseEnter={e => {
				setIsHovering(true);
				props.onMouseEnter?.(e);
			}}
			onMouseOver={e => {
				setIsHovering(true);
				props.onMouseOver?.(e);
			}}
			onMouseLeave={e => {
				setIsHovering(false);
				props.onMouseLeave?.(e);
			}}
		>
			<CardTop>
				<CardTopText>
					{isNotNil(patient) && <PatientButton patient={patient} />}
					<Title className="p-size-l-bold">{title}</Title>
					<Description>
						{format("patientDetails.patientPrograms.programCard.description.intro") + SPACE_SEPARATOR}
						<span className="p-size-s-bold">
							{format("patientDetails.patientPrograms.programCard.description.sessionsNumber", {
								count: sessionsPerWeek,
							}) + SPACE_SEPARATOR}
						</span>
						<Separator />
						<span className="p-size-s-bold">
							{SPACE_SEPARATOR +
								format("patientDetails.patientPrograms.programCard.description.exercisesNumber", {
									count: exercisesPerWeek,
								})}
						</span>
					</Description>
					{isNotNil(lastEditionMetadata) && (
						<div>
							{format("patientDetails.patientPrograms.programCard.lastEdited.v2_6", {
								date: formatRelativeDateTime(lastEditionMetadata.date),
								name:
									lastEditionMetadata.editor.id === user?.id
										? format("patientDetails.patientPrograms.programCard.lastEdited.you")
										: formatName(lastEditionMetadata.editor),
							})}
						</div>
					)}
				</CardTopText>
				<ActionButtonContainer>
					{(isHovering || isTouchDevice) && cardActions && cardActions.length > 0 && (
						<ActionButton menuId={props.id} menuItems={cardActions} />
					)}
				</ActionButtonContainer>
			</CardTop>
			{isNotNil(status) && <CardBottom>{status}</CardBottom>}
		</Container>
	);
};

const Container = styled.div<{ isNew: boolean }>`
	height: 100%;
	padding: 16px 16px 16px 32px;
	background-color: ${colors.white};
	border-radius: 8px;
	${stack("SM", "spacebetween", "stretch")};

	animation: 1s linear 2s fadeOut;
	animation-fill-mode: forwards;

	border: 1px solid ${({ isNew }) => (isNew ? colors.primary : colors.primary25)};
	box-shadow: 0 0 5px ${({ isNew }) => (isNew ? colorWithAlpha(colors.primary25, 0.5) : colors.white)};

	@keyframes fadeOut {
		100% {
			border-color: ${colors.primary25};
			box-shadow: 0 0 5px ${colors.white};
		}
	}

	${props =>
		props.onClick &&
		css`
			cursor: pointer;

			:hover {
				border: 1px solid ${colors.primary} !important;
			}
		`};
`;

const CardTop = styled.div`
	${row(undefined, "spacebetween", "center")};
	min-height: 40px;
`;

const CardTopText = styled.div`
	${stack("XSS", "flexstart")};
`;

const CardBottom = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	align-items: start;
`;

const ActionButtonContainer = styled.div`
	width: 40px;
	height: 100%;
	flex-shrink: 0;
`;

const ActionButton = styled(MenuButton)`
	.menu-button {
		background-color: ${colors.transparent};

		&.hover {
			background-color: ${colors.grey10};
		}
	}
`;

const Title = styled.h4`
	margin-right: 8px;
	overflow-wrap: anywhere;
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
`;

const Description = styled.span`
	color: ${colors.grey};
	padding-top: 4px;
`;

const Separator = styled.span`
	display: inline-block;
	height: 1rem;
	width: 0.0625rem;
	background-color: ${colors.primary25};
	margin-bottom: -0.1875rem;
`;
